import { useEffect, useState } from "react";
// CUSTOM COMPONENTS
import { IQueue } from "@/Settings/types/QueueMapping/QueueMapping";

// CONSTANTS
import { DEFAULT_QUEUE_NAME, EMPTY_NAME, MAX_OVERFLOW_QUEUE } from "@/Settings/Constants/QueueMapping";
// ICONS
import { Grid } from "@mui/material";
import MultipleSelectGroup from "@/Layouts/components/MultipleSelectGroup/MultipleSelectGroup";
import { buildQueueLabelsObjectList, isAlphanumeric } from "@/Settings/helpers/QueueMapping";

interface IQueueMappingOverflowProps {
	queues: IQueue[];
	mainQueue: IQueue;
	handleUpdateOverflowQueues: (overflowQueues: string[]) => void;
	isOverflowChecked: boolean;
	queueLabels: string[];
}

interface IOverflowEntry {
	_id: string;
	label: string;
}

const handleDisableAddButton = (overflowQueues: string[], overFlowQueueErrors: boolean[]) => {
	const hasMaxOverflowQueues = overflowQueues.length >= MAX_OVERFLOW_QUEUE;
	const hasEmptyNameWithoutErrors = overFlowQueueErrors.length === 0 && overflowQueues.includes(EMPTY_NAME);
	const hasMultipleErrors = overFlowQueueErrors.length > 1 && overFlowQueueErrors.includes(true);

	return hasMaxOverflowQueues || hasEmptyNameWithoutErrors || hasMultipleErrors;
};

const QueueMappingOverflow = (props: IQueueMappingOverflowProps) => {
	const { queues, mainQueue, handleUpdateOverflowQueues, isOverflowChecked, queueLabels } = props;

	const [overflowQueues, setOverflowQueues] = useState<string[]>(
		mainQueue.overflowQueues?.length ? mainQueue.overflowQueues : [EMPTY_NAME]
	);
	const [overFlowQueueErrors, setOverFlowQueueErrors] = useState<boolean[]>(
		mainQueue.overflowQueues?.length ? mainQueue.overflowQueues.map(() => false) : [true]
	);

	const validateOverflowEntry = (value: string) => {
		const valueToSearch = value.trim();
		const isInvalidName = valueToSearch === EMPTY_NAME || valueToSearch === DEFAULT_QUEUE_NAME || valueToSearch === mainQueue.name;
		const isLongName = valueToSearch.length > 100;
		const isAlphanumericName = isAlphanumeric(valueToSearch);

		if (isInvalidName || isLongName || !isAlphanumericName) {
			return true;
		}

		const queueNames = queues.map((queue) => queue.name);

		const existQueueAlready = queueNames.includes(valueToSearch);

		if (existQueueAlready) {
			return true;
		}

		const existingOverFlowQueues = queues.map((queue) => queue.overflowQueues).flat();

		const existOverflowQueueAlready = existingOverFlowQueues.includes(valueToSearch);

		if (existOverflowQueueAlready || overflowQueues.includes(valueToSearch)) {
			return true;
		}

		return false;
	};

	const handleAddOverflowQueue = () => {
		if (overflowQueues.length >= MAX_OVERFLOW_QUEUE) {
			return;
		}

		if (overFlowQueueErrors.includes(true)) {
			return;
		}

		setOverflowQueues([...overflowQueues, EMPTY_NAME]);
		setOverFlowQueueErrors([...overFlowQueueErrors, true]);
	};

	const handleOnChange = (selected: IOverflowEntry, idx: number) => {
		let cleanedLabel = selected.label.trim();
		if (selected.label.startsWith("Add ")) {
			cleanedLabel = selected.label.replace("Add ", "").replaceAll('"', "").trim();
		}
		const error = validateOverflowEntry(cleanedLabel);

		const updatedOverflowQueues = overflowQueues.map((queue, index) => (index === idx ? cleanedLabel : queue));
		setOverflowQueues(updatedOverflowQueues);

		if (error) {
			setOverFlowQueueErrors([...overFlowQueueErrors, error]);
			return;
		}

		handleUpdateOverflowQueues(updatedOverflowQueues);
	};

	const handleDeleteOverflowQueue = (idx: number) => {
		const updatedOverflowEntries = overflowQueues.filter((_, index) => index !== idx);
		setOverflowQueues(updatedOverflowEntries);
		handleUpdateOverflowQueues(updatedOverflowEntries);
	};

	useEffect(() => {
		setOverflowQueues(mainQueue.overflowQueues?.length ? mainQueue.overflowQueues : [EMPTY_NAME]);
		setOverFlowQueueErrors(mainQueue.overflowQueues?.length ? mainQueue.overflowQueues.map(() => false) : [true]);
	}, [mainQueue]);

	return (
		<Grid item className="position-mapping-payroll-column" position="relative" pl={1} pt={2}>
			{isOverflowChecked && (
				<MultipleSelectGroup
					allowNewValue
					allowDeleteFirst
					disableClearable
					showDeleteIcon
					selectBy={"label"}
					showBy={"label"}
					orderBy={"label"}
					placeholder={"Select an Option"}
					selectableOptions={buildQueueLabelsObjectList(queueLabels)}
					items={overflowQueues}
					onDelete={handleDeleteOverflowQueue}
					onChange={handleOnChange}
					onAdd={handleAddOverflowQueue}
					disableAddButton={handleDisableAddButton(overflowQueues, overFlowQueueErrors)}
					className="!w-[213px]"
					errorsList={overFlowQueueErrors}
				/>
			)}
		</Grid>
	);
};

export default QueueMappingOverflow;
